/**
 * Services catalog. This is the single source of truth for
 * service listing pages, service detail pages, and all navigation.
 */

const asset = (name) => `/generated/${name}.png`;

export const SERVICES = [
  {
    slug: "post-renovation-cleaning",
    name: "Post Renovation Cleaning",
    tagline: "Remove Every Trace of Renovation.",
    eyebrow: "POST RENOVATION CLEANING",
    headline: "Move Into a Clean, Dust-Free Home.\nAs It Was Meant to Be.",
    shortDescription:
      "Remove renovation dust, paint marks, cement residue and construction debris.",
    intro:
      "Renovation leaves behind more than debris — fine dust settles into every corner, air-con vents, cabinet joinery and skirting. Our post-renovation cleaning is a thorough top-to-bottom detail wipe designed for freshly completed homes and units.",
    hero: asset("service_post_reno"),
    category: ["Residential", "Commercial", "Renovation"],
    icon: "sparkles",
    benefits: [
      { title: "Homeowners", body: "Move into a fully hygienic home." },
      { title: "Landlords", body: "Handover units in showroom-clean condition." },
      { title: "Interior Designers", body: "Present your work at its very best." },
      { title: "Fine Dust Removal", body: "HEPA vacuum and detail wipe every surface." },
      { title: "Cement & Paint Marks", body: "Safely lifted from tiles, glass and fixtures." },
      { title: "Experienced Team", body: "Trained specifically for post-reno detail work." },
    ],
    included: [
      "HEPA vacuuming of floors, skirting, ceiling corners",
      "Interior and exterior wipe of all carpentry",
      "Removal of cement and paint marks from tiles, glass, fixtures",
      "Wiping of window grilles, tracks and frames",
      "Bathroom deep clean including grout",
      "Kitchen top-to-bottom detail wipe",
      "Air-con fan and grille wipe (external)",
      "Final dust-free walk-through inspection",
    ],
    suitableFor: ["HDB / BTO", "Condominiums", "Landed Homes", "Showflats", "Commercial Units"],
    relatedSlugs: ["end-of-tenancy-cleaning", "deep-floor-cleaning", "formaldehyde-removal"],
    faqs: [
      {
        q: "When is the best time to book?",
        a: "Ideally 1–3 days after renovation completion, once contractors have removed heavy debris.",
      },
      {
        q: "Do you clean air-conditioning units?",
        a: "We wipe external fan units, grilles and covers. Full chemical A/C servicing is a separate service.",
      },
    ],
  },
  {
    slug: "end-of-tenancy-cleaning",
    name: "End of Tenancy Cleaning",
    tagline: "Leave Your Property Spotless.",
    eyebrow: "END OF TENANCY CLEANING",
    headline: "Leave Your Property Spotless.\nImpress Landlords & New Occupants.",
    shortDescription:
      "Prepare a property for handover, inspection or its next occupant.",
    intro:
      "Moving out can be stressful enough without cleaning. Our end-of-tenancy service restores your property to a clean, hygienic and presentable condition — ready for handover, inspection or the next occupant.",
    hero: asset("service_end_tenancy"),
    category: ["Residential", "Commercial"],
    icon: "key-round",
    benefits: [
      { title: "Tenants Moving Out", body: "Leave the property in excellent condition." },
      { title: "Landlords", body: "Prepare your unit for new tenants." },
      { title: "Property Agents", body: "Get your units viewing-ready faster." },
      { title: "Deep & Thorough", body: "Every corner, surface and area covered." },
      { title: "Professional Equipment", body: "High-quality tools and cleaning products." },
      { title: "Experienced Team", body: "Trained professionals with attention to detail." },
    ],
    included: [
      "Full kitchen degrease including hood and hob",
      "Bathroom scale and grout deep clean",
      "Window and sliding door interior clean",
      "Wipe of built-in wardrobes and shelving",
      "Vacuum and mop of all floors",
      "Fridge and oven interior clean (if left onsite)",
      "Cobweb, dust and skirting detail",
    ],
    suitableFor: ["HDB / BTO", "Condominiums", "Landed Homes", "Serviced Apartments"],
    relatedSlugs: ["post-renovation-cleaning", "deep-floor-cleaning", "sofa-cleaning"],
    faqs: [
      {
        q: "Can you clean while furniture is still in the unit?",
        a: "Yes, though moving out furniture first allows a more thorough clean.",
      },
    ],
  },
  {
    slug: "deep-floor-cleaning",
    name: "Deep Floor Cleaning",
    tagline: "Restore Your Floors.",
    eyebrow: "DEEP FLOOR CLEANING",
    headline: "Machine-Scrubbed Floors.\nRestored Depth. Restored Shine.",
    shortDescription:
      "Remove embedded dirt using professional machine-scrubbing equipment.",
    intro:
      "Everyday mopping cannot reach dirt embedded in grout lines, tile pores and stone surfaces. Our commercial rotary scrubbers and pH-balanced solutions restore floors that look tired or dull.",
    hero: asset("service_deep_floor"),
    category: ["Residential", "Commercial", "Floor and Exterior"],
    icon: "layout-dashboard",
    benefits: [
      { title: "Homogenous & Marble", body: "Restore original stone depth." },
      { title: "Porcelain & Ceramic", body: "Lift ground-in dirt from grout." },
      { title: "Commercial Floors", body: "Bring back showroom shine." },
      { title: "Machine-Scrubbed", body: "Rotary equipment does the deep work." },
      { title: "Water Extraction", body: "Fast dry — back in use quickly." },
      { title: "Safe Chemistry", body: "pH-balanced for your floor type." },
    ],
    included: [
      "Pre-inspection of floor material and condition",
      "Machine scrubbing with appropriate pad and chemistry",
      "Grout-line detail",
      "Water extraction and dry",
      "Optional light polish or sealing (on request)",
    ],
    suitableFor: ["Marble", "Porcelain", "Homogenous Tiles", "Vinyl", "Commercial Floors"],
    relatedSlugs: ["post-renovation-cleaning", "pressure-washing", "disinfection-service"],
    faqs: [
      {
        q: "Will this remove all stains?",
        a: "Deep cleaning removes most surface stains and embedded dirt. Etched stone or deeply-set marks may require polishing.",
      },
    ],
  },
  {
    slug: "formaldehyde-removal",
    name: "Formaldehyde Removal",
    tagline: "Breathe Easier at Home.",
    eyebrow: "FORMALDEHYDE REMOVAL",
    headline: "A Cleaner Air Indoors.\nAfter Every Renovation.",
    shortDescription:
      "Improve indoor air quality after renovation or installation of new carpentry.",
    intro:
      "Newly installed carpentry, wardrobes and flooring can release formaldehyde and other VOCs into your home. Our specialised indoor air treatment reduces these compounds through professional-grade misting and photo-catalytic coatings.",
    hero: asset("service_formaldehyde"),
    category: ["Residential", "Indoor Air Quality"],
    icon: "wind",
    benefits: [
      { title: "Newly Renovated Homes", body: "Treat freshly installed carpentry." },
      { title: "Families With Young Children", body: "Reduce indoor VOCs." },
      { title: "Sensitive Occupants", body: "Suited for asthma and allergy concerns." },
      { title: "Photo-catalytic Coating", body: "Long-lasting surface treatment." },
      { title: "Professional Equipment", body: "Fine electrostatic misting." },
      { title: "Trained Team", body: "Handled by experienced technicians." },
    ],
    included: [
      "Assessment of affected areas",
      "Professional fine-mist application to carpentry and enclosed surfaces",
      "Photo-catalytic coating (where applicable)",
      "Ventilation guidance",
    ],
    suitableFor: ["HDB / BTO", "Condominiums", "Newly Renovated Homes", "Showflats"],
    relatedSlugs: ["post-renovation-cleaning", "disinfection-service", "deep-floor-cleaning"],
    faqs: [
      {
        q: "Do you guarantee complete formaldehyde elimination?",
        a: "No professional service can guarantee 100% elimination — but our treatment materially reduces indoor VOCs after renovation.",
      },
    ],
  },
  {
    slug: "mattress-cleaning",
    name: "Mattress Cleaning",
    tagline: "Sleep on a Truly Clean Mattress.",
    eyebrow: "MATTRESS CLEANING",
    headline: "Deeper Sleep.\nCleaner Mattress.",
    shortDescription:
      "Reduce dust mites, allergens, bacteria, odours and surface stains.",
    intro:
      "Mattresses accumulate dust mites, dead skin cells, sweat and bacteria. Our professional UV + HEPA extraction reduces allergens and refreshes your mattress without soaking or over-wetting.",
    hero: asset("service_mattress"),
    category: ["Residential", "Upholstery"],
    icon: "bed",
    benefits: [
      { title: "Families & Children", body: "Reduce common allergens." },
      { title: "Allergy Sufferers", body: "Lift dust mites and dander." },
      { title: "Odour Removal", body: "Freshen and deodorise fibres." },
      { title: "Surface Stains", body: "Treat visible marks safely." },
      { title: "UV Sanitisation", body: "Professional UV pass." },
      { title: "Dry Same-Day", body: "Low-moisture process." },
    ],
    included: [
      "Inspection and pre-treatment of stains",
      "UV surface pass",
      "HEPA vacuum extraction",
      "Deodoriser application",
      "Both sides where possible",
    ],
    suitableFor: ["Single", "Queen", "King", "Cot / Baby Mattress"],
    relatedSlugs: ["sofa-cleaning", "disinfection-service", "deep-floor-cleaning"],
    faqs: [{ q: "How long does it take?", a: "Approximately 45–60 minutes per mattress." }],
  },
  {
    slug: "sofa-cleaning",
    name: "Sofa Cleaning",
    tagline: "Refresh Your Living Room.",
    eyebrow: "SOFA CLEANING",
    headline: "Refreshed Fabric.\nRestored Comfort.",
    shortDescription:
      "Refresh upholstery and remove accumulated dirt, allergens and stains.",
    intro:
      "Sofas absorb dust, oils and everyday spills. Our low-moisture extraction lifts embedded dirt and treats stains — restoring the look and feel of your upholstery.",
    hero: asset("service_sofa"),
    category: ["Residential", "Upholstery"],
    icon: "sofa",
    benefits: [
      { title: "Fabric Sofas", body: "Safe extraction on most weaves." },
      { title: "Family Homes", body: "Kid-friendly, low-moisture process." },
      { title: "Stain Treatment", body: "Targeted pre-treatment on marks." },
      { title: "Odour Removal", body: "Freshen with light deodoriser." },
      { title: "Dry Same-Day", body: "Back in use quickly." },
      { title: "Trained Team", body: "Experienced with upholstery care." },
    ],
    included: [
      "Fabric inspection and pre-treatment",
      "Upholstery extraction with hose attachment",
      "Cushion covers where safely removable",
      "Deodoriser application",
    ],
    suitableFor: ["1-seater", "2-seater", "3-seater", "L-shaped", "Sectional"],
    relatedSlugs: ["mattress-cleaning", "deep-floor-cleaning", "disinfection-service"],
    faqs: [
      {
        q: "Do you clean leather sofas?",
        a: "We focus on fabric upholstery. Leather requires a separate specialised process.",
      },
    ],
  },
  {
    slug: "pressure-washing",
    name: "Pressure Washing",
    tagline: "Strip Away Years of Grime.",
    eyebrow: "PRESSURE WASHING",
    headline: "High-Pressure Clean.\nOutdoor Surfaces, Restored.",
    shortDescription:
      "Remove algae, moss, dirt and grime from suitable outdoor surfaces.",
    intro:
      "Driveways, patios, external walls and commercial entrances accumulate algae, moss and stubborn grime. Our high-pressure equipment lifts years of build-up quickly and safely.",
    hero: asset("service_pressure"),
    category: ["Residential", "Commercial", "Floor and Exterior"],
    icon: "droplets",
    benefits: [
      { title: "Landed Homes", body: "Driveways, patios, walkways." },
      { title: "Commercial", body: "Entrances, loading bays, carparks." },
      { title: "Algae & Moss", body: "Safely lifted from tiles and concrete." },
      { title: "Grime Removal", body: "Refresh weathered surfaces." },
      { title: "Professional PSI", body: "Correct pressure per surface." },
      { title: "Experienced Team", body: "Trained on outdoor work." },
    ],
    included: [
      "Pre-inspection of surface type",
      "High-pressure wash with suitable nozzle",
      "Targeted detergent for algae / oil",
      "Rinse and sweep",
    ],
    suitableFor: ["Driveways", "Patios", "External Walls", "Carparks", "Commercial Entrances"],
    relatedSlugs: ["deep-floor-cleaning", "post-renovation-cleaning", "disinfection-service"],
    faqs: [
      {
        q: "Is pressure washing safe on all surfaces?",
        a: "Not all — soft stone, delicate paint and old grout may need lower-pressure alternatives. We inspect first.",
      },
    ],
  },
  {
    slug: "disinfection-service",
    name: "Disinfection Service",
    tagline: "A Cleaner, Safer Space.",
    eyebrow: "DISINFECTION SERVICE",
    headline: "Reduce Germs on Every Surface.\nHome or Office.",
    shortDescription:
      "Reduce bacteria and viruses on high-touch residential and commercial surfaces.",
    intro:
      "Electrostatic disinfection covers high-touch surfaces evenly with hospital-grade solution — ideal for homes, offices, retail and F&B environments.",
    hero: asset("service_disinfection"),
    category: ["Residential", "Commercial", "Indoor Air Quality"],
    icon: "shield-check",
    benefits: [
      { title: "Homes", body: "After illness or as maintenance." },
      { title: "Offices", body: "Regular workplace disinfection." },
      { title: "Retail & F&B", body: "Fine-mist coverage on all surfaces." },
      { title: "Electrostatic Fog", body: "Even, all-around coverage." },
      { title: "Hospital-Grade Solution", body: "Effective against common pathogens." },
      { title: "Trained Team", body: "PPE and correct dwell times." },
    ],
    included: [
      "Pre-check of enclosed areas",
      "Electrostatic fogging application",
      "High-touch surface wipe",
      "Ventilation guidance",
    ],
    suitableFor: ["HDB / BTO", "Condominiums", "Offices", "Retail", "Restaurants"],
    relatedSlugs: ["formaldehyde-removal", "deep-floor-cleaning", "post-renovation-cleaning"],
    faqs: [
      {
        q: "How long before the space is safe to re-enter?",
        a: "Typically 30–60 minutes depending on ventilation.",
      },
    ],
  },
  {
    slug: "defect-inspection",
    name: "Defect Inspection",
    tagline: "Spot Issues Before It's Too Late.",
    eyebrow: "DEFECT INSPECTION",
    headline: "Identify Defects Early.\nProtect Your Investment.",
    shortDescription:
      "Identify visible property defects before the defects liability period ends.",
    intro:
      "For new-launch condominiums and BTO handovers, a structured defect check documents visible issues so you can raise them within your defects liability period.",
    hero: asset("service_defect"),
    category: ["Residential", "Inspection"],
    icon: "clipboard-check",
    benefits: [
      { title: "New BTO Handover", body: "Before you move in." },
      { title: "New Launch Condos", body: "Before your DLP ends." },
      { title: "Landed Handover", body: "Structured room-by-room check." },
      { title: "Moisture & Cracks", body: "Meter and torch-based inspection." },
      { title: "Documented Report", body: "Photos and location notes." },
      { title: "Trained Inspector", body: "Familiar with common issues." },
    ],
    included: [
      "Room-by-room visible defect check",
      "Torch and moisture meter inspection",
      "Photo documentation",
      "Written summary with locations",
    ],
    suitableFor: ["BTO", "New Launch Condos", "Landed Homes", "Showflats"],
    relatedSlugs: ["post-renovation-cleaning", "end-of-tenancy-cleaning", "deep-floor-cleaning"],
    faqs: [
      {
        q: "Do you guarantee detection of every defect?",
        a: "No inspection can guarantee 100% detection. We focus on visible defects and document them thoroughly.",
      },
    ],
  },
];

export const CATEGORIES = [
  "All",
  "Residential",
  "Commercial",
  "Renovation",
  "Upholstery",
  "Floor and Exterior",
  "Indoor Air Quality",
  "Inspection",
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
export const getRelated = (slug) => {
  const s = getService(slug);
  if (!s) return [];
  return s.relatedSlugs.map((sl) => getService(sl)).filter(Boolean);
};
